import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Input } from '@/ui/input'
import { Button } from '@/ui/button'
import { Label } from '@/ui/label'
import { Skeleton } from '@/ui/skeleton'
import { MaterialIcon } from '@/ui/material-icon'
import { cn } from '@/shared/lib/utils'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { getRegisteredTables, searchEntity } from '@/features/adp/services/api-service'
import type { RegisteredTargetTable } from '@/features/adp/services/api-service.types'
import type { RegisteredSourcePickerItem } from '@/features/adp/hooks/useRegisteredDataSources'
import {
  buildConnectionDetailsFromRegisteredListItem,
  firstNonEmptyTrimmed,
  firstUsableConnectionString,
  normalizePortString,
  supportsSchemaForInstance,
  type RegisteredListConnectionDetails,
} from './helpers'
import styles from './ConfigureDataDestinationSheet.module.scss'

export type RegisteredDestinationConnectionDetails = RegisteredListConnectionDetails

type RegisteredDestinationTablesFieldsProps = {
  targetDataSourceId: string
  /** Full row from `GET /target-data-sources/registered` (mapped) for use before/without tables API. */
  listDestination: RegisteredSourcePickerItem | null
  /** Restored from session draft after reopening the destination sheet. */
  restoredTableName?: string
  /** Restored from session draft. */
  restoredTableSelectionMode?: 'existing' | 'new' | null
  /** When true, table step was saved: show values only (no list / add new / save). */
  isTableSelectionLocked?: boolean
  destinationName: string
  databaseName: string
  schemaName: string
  isSubmittingTarget: boolean
  onSaveTableSelection?: (
    tableName: string,
    selectionMode: 'existing' | 'new',
    connectionDetails: RegisteredDestinationConnectionDetails | null,
    /** Set when selecting a row from Table List — used to skip target-tables batch API. */
    existingTableIds: { targetDataSourceId: number; targetTableId: number } | null
  ) => void
}

type RegisteredTableFieldLike = {
  target_table_column_id?: number
  column_name?: string
  field_name?: string
  data_type?: string
  field_data_type?: string
}

const readOnlyClass = 'focus-visible:ring-0 focus-visible:outline-none focus:ring-0'
const tableSelectionSchema = z.object({
  selectedRegisteredTableName: z.string().default(''),
  newTableName: z.string().default(''),
})

type TableSelectionFormValues = z.infer<typeof tableSelectionSchema>

function normalizeTableName(value: string): string {
  return value
    .trim()
    .replaceAll(/\W+/g, '_')
    .replaceAll(/_+/g, '_')
    .replaceAll(/^_+|_+$/g, '');
}

function tableFieldDefaults(
  mode: 'existing' | 'new' | null | undefined,
  name: string | undefined
): TableSelectionFormValues {
  const n = name?.trim() ?? ''
  if (mode === 'existing') {
    return { selectedRegisteredTableName: n, newTableName: '' }
  }
  if (mode === 'new') {
    return { selectedRegisteredTableName: '', newTableName: n }
  }
  if (n) {
    return { selectedRegisteredTableName: '', newTableName: n }
  }
  return { selectedRegisteredTableName: '', newTableName: '' }
}

export function RegisteredDestinationTablesFields({
  targetDataSourceId,
  listDestination,
  restoredTableName = '',
  restoredTableSelectionMode = null,
  isTableSelectionLocked = false,
  destinationName,
  databaseName,
  schemaName,
  isSubmittingTarget,
  onSaveTableSelection,
}: Readonly<RegisteredDestinationTablesFieldsProps>) {
  const [tableSearch, setTableSearch] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const form = useForm<TableSelectionFormValues>({
    resolver: zodResolver(tableSelectionSchema),
    defaultValues: tableFieldDefaults(restoredTableSelectionMode, restoredTableName),
  })

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['adp', 'registered-target-tables', targetDataSourceId],
    enabled: Boolean(targetDataSourceId.trim()) && !isTableSelectionLocked,
    queryFn: () => getRegisteredTables(targetDataSourceId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const tables = data?.data?.listOfTargetFiles ?? []
  const fromList = useMemo<RegisteredDestinationConnectionDetails | null>(
    () =>
      listDestination
        ? buildConnectionDetailsFromRegisteredListItem({
          databaseName: listDestination.databaseName,
          username: listDestination.username,
          password: listDestination.password,
          endpoint: listDestination.endpoint ?? '',
        })
        : null,
    [listDestination]
  )
  const registeredDatabaseDetails = useMemo<RegisteredDestinationConnectionDetails | null>(() => {
    const db = data?.data?.databases?.[0]
    if (db) {
      return {
        host: firstNonEmptyTrimmed(db.host, fromList?.host) ?? '',
        port:
          firstNonEmptyTrimmed(
            normalizePortString(db.port),
            fromList?.port
          ) ?? '',
        database: firstNonEmptyTrimmed(db.database, fromList?.database) ?? '',
        username: firstNonEmptyTrimmed(db.username, fromList?.username) ?? '',
        password: firstNonEmptyTrimmed(db.password, fromList?.password) ?? '',
        schema: supportsSchemaForInstance(listDestination?.dataSourceInstance)
          ? (firstUsableConnectionString(db.schema, fromList?.schema) ?? '')
          : '',
      }
    }
    if (!fromList) return null
    return {
      ...fromList,
      schema: supportsSchemaForInstance(listDestination?.dataSourceInstance) ? fromList.schema : '',
    }
  }, [data?.data?.databases, fromList, listDestination?.dataSourceInstance])
  const selectedRegisteredTableName = form.watch('selectedRegisteredTableName')
  const newTableName = form.watch('newTableName')
  const debouncedNewTableName = useDebounce(newTableName, 500)

  const {
    data: tableSearchValidation,
    isFetching: isCheckingNewTable,
    isError: isNewTableValidationErrored,
    error: newTableValidationError,
  } = useQuery({
    queryKey: ['adp', 'search-entity', 'target_table', debouncedNewTableName],
    enabled: Boolean(debouncedNewTableName.trim()) && !isTableSelectionLocked,
    retry: false,
    queryFn: () => searchEntity('target_table', debouncedNewTableName),
  })

  const filteredTables = useMemo(() => {
    const probe = tableSearch.trim().toLowerCase()
    if (!probe) return tables
    return tables.filter(table => table.table_name?.toLowerCase().includes(probe))
  }, [tableSearch, tables])

  const allExpanded = filteredTables.length > 0 && filteredTables.every(t => expandedIds.has(t.target_table_id))

  const toggleExpand = (targetTableId: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(targetTableId)) next.delete(targetTableId)
      else next.add(targetTableId)
      return next
    })
  }

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set())
      return
    }
    setExpandedIds(new Set(filteredTables.map(t => t.target_table_id)))
  }

  const handleSelectRegisteredTable = (table: RegisteredTargetTable) => {
    form.setValue('selectedRegisteredTableName', table.table_name ?? '')
    form.setValue('newTableName', '')
    // onTableNameChange(table.table_name ?? '')
  }

  const handleChangeNewTable = (value: string) => {
    form.setValue('newTableName', value)
    form.setValue('selectedRegisteredTableName', '')
    // onTableNameChange(value)
  }

  const hasSelectedRegisteredTable = Boolean(selectedRegisteredTableName.trim())
  const hasNewTableName = Boolean(newTableName.trim())
  const isSelectedFromNewTable = hasNewTableName
  const normalizedSelectedTableName = normalizeTableName(selectedRegisteredTableName)
  const hasValidationResult = Boolean(debouncedNewTableName.trim())
  const canUseTypedTable =
    !hasNewTableName ||
    (Boolean(tableSearchValidation?.success) && !isCheckingNewTable);
  const isSaveDisabled =
    isSubmittingTarget ||
    (!hasSelectedRegisteredTable && !hasNewTableName) ||
    (hasNewTableName && !canUseTypedTable);

  const handleSave = () => {
    const typedTableName = newTableName.trim()
    const selectedTableName = selectedRegisteredTableName.trim()
    const tableName = typedTableName || selectedTableName
    if (!tableName) return
    if (typedTableName) {
      onSaveTableSelection?.(tableName, 'new', registeredDatabaseDetails, null)
      return
    }
    const match = tables.find(
      t => (t.table_name ?? '').trim() === selectedTableName
    )
    const targetDataSourceId = data?.data?.target_data_source_id
    const existingTableIds =
      match &&
        typeof targetDataSourceId === 'number' &&
        Number.isFinite(targetDataSourceId) &&
        Number.isFinite(match.target_table_id)
        ? { targetDataSourceId, targetTableId: match.target_table_id }
        : null
    onSaveTableSelection?.(tableName, 'existing', registeredDatabaseDetails, existingTableIds)
  }

  const displayDatabase =
    databaseName.trim() || (isTableSelectionLocked ? '\u2014' : '')
  const displaySchema =
    supportsSchemaForInstance(listDestination?.dataSourceInstance)
      ? (schemaName.trim() || (isTableSelectionLocked ? '\u2014' : ''))
      : (isTableSelectionLocked ? '\u2014' : '')
  const isExcelDestination = (listDestination?.dataSourceInstance ?? '').trim().toLowerCase() === 'excel'
  const displayTableName =
    restoredTableName.trim() ||
    newTableName.trim() ||
    selectedRegisteredTableName.trim() ||
    (isTableSelectionLocked ? '\u2014' : '')

  return (
    <>
      <div className={styles.field}>
        <Label className={styles.fieldLabel}>Destination Name</Label>
        <Input
          value={destinationName}
          readOnly
          disabled={isTableSelectionLocked}
          aria-readonly="true"
          className={readOnlyClass}
        />
      </div>

      <div className={styles.twoColRow}>
        <div className={styles.field}>
          <Label className={styles.fieldLabel}>Database Name</Label>
          <Input
            value={displayDatabase}
            readOnly
            disabled={isTableSelectionLocked}
            aria-readonly="true"
            className={readOnlyClass}
          />
        </div>
        <div className={styles.field}>
          <Label className={styles.fieldLabel}>Schema Name</Label>
          <Input
            value={displaySchema}
            readOnly
            disabled={isTableSelectionLocked}
            aria-readonly="true"
            className={readOnlyClass}
          />
        </div>
      </div>

      {isTableSelectionLocked ? (
        <div className={styles.field}>
          <Label className={styles.fieldLabel}>Table name</Label>
          <Input
            value={displayTableName}
            readOnly
            disabled
            aria-readonly="true"
            className={readOnlyClass}
          />
        </div>
      ) : null}

      {!isTableSelectionLocked ? (
        <>
          <div className={styles.registeredTableCard}>
            <div className={styles.registeredTableHeader}>
              <div className={styles.registeredTableTitle}>Table List</div>
              <div className={styles.registeredTableActions}>
                <div className={styles.registeredSearchWrap}>
                  <MaterialIcon name="search" size={16} />
                  <Input
                    value={tableSearch}
                    onChange={e => setTableSearch(e.currentTarget.value)}
                    className={styles.registeredSearchInput}
                    placeholder="Search Table Name"
                  />
                </div>
              </div>
            </div>

            <div className={styles.registeredListHeader}>
              <span>Table name</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(styles.expandAllBtn, styles.registeredActionBtn)}
                onClick={toggleExpandAll}
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </Button>
            </div>

            {isLoading && (
              <div className={styles.registeredTableSkeletonList}>
                {[0, 1, 2].map(item => (
                  <Skeleton key={item} className={styles.registeredTableSkeletonRow} />
                ))}
              </div>
            )}

            {!isLoading && isError && (
              <p className={styles.registeredTableState} role="alert">
                {error instanceof Error ? error.message : 'Failed to load registered tables.'}
              </p>
            )}

            {!isLoading && !isError && filteredTables.length === 0 && (
              <p className={styles.registeredTableState}>No registered tables found.</p>
            )}

            {!isLoading && !isError && filteredTables.length > 0 && (
              <div className={styles.registeredTableRows}>
                {filteredTables.map((table) => {
                  const isExpanded = expandedIds.has(table.target_table_id)
                  const isSelected =
                    !isSelectedFromNewTable &&
                    normalizeTableName(table.table_name ?? '') === normalizedSelectedTableName

                  return (
                    <div key={table.target_table_id} className={styles.registeredTableRowWrap}>
                      <div className={styles.registeredTableRow}>
                        <label className={styles.registeredTableSelect}>
                          <input
                            type="radio"
                            name="registered-table"
                            checked={isSelected}
                            onChange={() => handleSelectRegisteredTable(table)}
                          />
                          {table.table_name}
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={styles.registeredExpandBtn}
                          onClick={() => toggleExpand(table.target_table_id)}
                        >
                          <MaterialIcon
                            name={isExpanded ? 'expand_less' : 'expand_more'}
                            size={16}
                            aria-hidden
                          />
                          {isExpanded ? 'Collapse' : 'Expand'}
                        </Button>
                      </div>
                      {isExpanded && (
                        <div className={styles.registeredFieldRows}>
                          <div className={styles.registeredFieldsTable}>
                            <div className={styles.registeredFieldsHeaderCell}>Field Name</div>
                            <div className={styles.registeredFieldsHeaderCell}>Data Type</div>

                            {(table.fields ?? []).length > 0 ? (
                              (table.fields as RegisteredTableFieldLike[]).map((field, idx) => (
                                <div
                                  key={field.target_table_column_id ?? `${field.column_name ?? field.field_name ?? 'field'}-${idx}`}
                                  className={styles.registeredFieldsRowPair}
                                >
                                  <div className={styles.registeredFieldsBodyCell}>
                                    {field.column_name ?? field.field_name ?? '\u2014'}
                                  </div>
                                  <div className={styles.registeredFieldsBodyCell}>
                                    {field.data_type ?? field.field_data_type ?? '\u2014'}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className={styles.registeredFieldsEmptyCell}>No fields available.</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className={styles.registeredOrDivider} aria-hidden>
            <span className={styles.registeredOrLabel}>Or</span>
          </div>

          <div className={styles.registeredNewTableBlock}>
            <Label className={cn(styles.fieldLabel, styles.registeredNewTableTitle)}>Add New Table</Label>
            <div className={styles.field}>
              <Label className={styles.fieldLabel}>Table Name</Label>
              <Input
                value={newTableName}
                onChange={e => handleChangeNewTable(e.currentTarget.value)}
                placeholder="Enter table name"
                className={styles.textInput}
                disabled={isExcelDestination}
              />
              {hasValidationResult && isCheckingNewTable && (
                <p className={styles.registeredInlineState}>Validating table name...</p>
              )}
              {hasValidationResult && !isCheckingNewTable && tableSearchValidation && !tableSearchValidation.success && (
                <p className={styles.registeredInlineState} role="alert">
                  {tableSearchValidation.message || 'Table name is not available.'}
                </p>
              )}
              {hasValidationResult && isNewTableValidationErrored && (
                <p className={styles.registeredInlineState} role="alert">
                  {newTableValidationError instanceof Error
                    ? newTableValidationError.message
                    : 'Unable to validate table name right now.'}
                </p>
              )}
            </div>
          </div>

          <div className={cn(styles.actionsRow, styles.registeredActionsRow)}>
            <Button
              type="button"
              size="sm"
              className={styles.primaryBtn}
              disabled={isSaveDisabled}
              onClick={handleSave}
            >
              {isSubmittingTarget ? 'Please wait...' : 'Save'}
            </Button>
          </div>
          {isExcelDestination && (
            <p className={styles.registeredInlineState}>Add New Table is not available for Excel sources.</p>
          )}
        </>
      ) : null}
    </>
  )
}
